// src/components/CommonPartEditor.jsx
import React, { useState, useEffect } from 'react';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/mode-css';
import 'ace-builds/src-noconflict/theme-tomorrow_night';
import 'ace-builds/src-noconflict/ext-language_tools';
import ChatGPTCodeGenerator from './ChatGPTCodeGenerator';
import { PART_TYPES, DEFAULT_PART_TEMPLATES } from '../types/commonParts';
import '../styles/components/CommonPartEditor.scss';

const CommonPartEditor = ({ part, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: PART_TYPES.HEADER,
        html: '',
        css: ''
    });
    const [isChatGPTOpen, setIsChatGPTOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('html');
    const [error, setError] = useState('');

    // 編集時は既存データを設定
    useEffect(() => {
        if (part) {
            setFormData({
                name: part.name || '',
                description: part.description || '',
                type: part.type || PART_TYPES.HEADER,
                html: part.html || '',
                css: part.css || ''
            });
        } else {
            // 新規作成時はデフォルトテンプレートを使用
            const template = DEFAULT_PART_TEMPLATES[PART_TYPES.HEADER];
            setFormData(prev => ({
                ...prev,
                html: template.html,
                css: template.css
            }));
        }
    }, [part]);

    // フォームデータの更新
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // エディターの変更
    const handleEditorChange = (value, field) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // パーツタイプ変更時のテンプレート適用
    const handleTypeChange = (e) => {
        const newType = e.target.value;
        const template = DEFAULT_PART_TEMPLATES[newType];

        setFormData(prev => ({
            ...prev,
            type: newType,
            html: template ? template.html : prev.html,
            css: template ? template.css : prev.css
        }));
    };

    // ChatGPTから生成されたコードを適用
    const handleChatGPTCodeGenerated = (generatedCode) => {
        if (generatedCode.fullHTML) {
            setFormData(prev => ({
                ...prev,
                html: generatedCode.fullHTML
            }));
        }
        if (generatedCode.css) {
            setFormData(prev => ({
                ...prev,
                css: generatedCode.css
            }));
        }
        setIsChatGPTOpen(false);
    };

    // 保存処理
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.name.trim()) {
            setError('パーツ名を入力してください');
            return;
        }

        if (!formData.html.trim()) {
            setError('HTMLコンテンツを入力してください');
            return;
        }

        try {
            await onSave(formData);
        } catch (err) {
            console.error('保存エラー:', err);
            setError('保存に失敗しました: ' + err.message);
        }
    };

    // Ace Editorの設定
    const getAceEditorProps = (mode) => ({
        mode: mode,
        theme: 'tomorrow_night',
        fontSize: 14,
        width: '100%',
        height: '400px',
        setOptions: {
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: true,
            enableSnippets: false,
            showLineNumbers: true,
            tabSize: 2,
            useWorker: false
        }
    });

    return (
        <div className="common-part-editor">
            <form onSubmit={handleSubmit} className="part-form">
                {/* 基本情報 */}
                <div className="form-section">
                    <h3>基本情報</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="name">パーツ名 *</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="type">種類</label>
                            <select
                                id="type"
                                name="type"
                                value={formData.type}
                                onChange={handleTypeChange}
                            >
                                <option value={PART_TYPES.HEADER}>ヘッダー</option>
                                <option value={PART_TYPES.FOOTER}>フッター</option>
                                <option value={PART_TYPES.NAVIGATION}>ナビゲーション</option>
                                <option value={PART_TYPES.SIDEBAR}>サイドバー</option>
                                <option value={PART_TYPES.CTA}>CTA</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="description">説明</label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="3"
                            placeholder="このパーツの説明を入力してください"
                        />
                    </div>
                </div>

                {/* コンテンツ編集 */}
                <div className="form-section">
                    <div className="section-header">
                        <h3>コンテンツ編集</h3>
                        <button
                            type="button"
                            className="chatgpt-button"
                            onClick={() => setIsChatGPTOpen(true)}
                        >
                            🤖 AI生成
                        </button>
                    </div>

                    {/* タブ */}
                    <div className="editor-tabs">
                        <button
                            type="button"
                            className={`tab-button ${activeTab === 'html' ? 'active' : ''}`}
                            onClick={() => setActiveTab('html')}
                        >
                            HTML
                        </button>
                        <button
                            type="button"
                            className={`tab-button ${activeTab === 'css' ? 'active' : ''}`}
                            onClick={() => setActiveTab('css')}
                        >
                            CSS
                        </button>
                        <button
                            type="button"
                            className={`tab-button ${activeTab === 'preview' ? 'active' : ''}`}
                            onClick={() => setActiveTab('preview')}
                        >
                            プレビュー
                        </button>
                    </div>

                    {/* エディター */}
                    <div className="editor-content">
                        {activeTab === 'html' && (
                            <div className="editor-section">
                                <h4>HTML</h4>
                                <AceEditor
                                    {...getAceEditorProps('html')}
                                    value={formData.html}
                                    onChange={(value) => handleEditorChange(value, 'html')}
                                    name="html-editor"
                                />
                            </div>
                        )}

                        {activeTab === 'css' && (
                            <div className="editor-section">
                                <h4>CSS</h4>
                                <AceEditor
                                    {...getAceEditorProps('css')}
                                    value={formData.css}
                                    onChange={(value) => handleEditorChange(value, 'css')}
                                    name="css-editor"
                                />
                            </div>
                        )}

                        {activeTab === 'preview' && (
                            <div className="preview-section">
                                <h4>プレビュー</h4>
                                <div className="preview-container">
                                    <div
                                        className="preview-content"
                                        dangerouslySetInnerHTML={{
                                            __html: formData.html
                                        }}
                                    />
                                    {formData.css && (
                                        <style>{formData.css}</style>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* エラーメッセージ */}
                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                {/* アクションボタン */}
                <div className="form-actions">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="cancel-button"
                    >
                        キャンセル
                    </button>
                    <button
                        type="submit"
                        className="save-button"
                    >
                        {part ? '更新' : '作成'}
                    </button>
                </div>
            </form>

            {/* ChatGPTコード生成モーダル */}
            <ChatGPTCodeGenerator
                isOpen={isChatGPTOpen}
                onClose={() => setIsChatGPTOpen(false)}
                onCodeGenerated={handleChatGPTCodeGenerated}
                partType={formData.type}
                partName={formData.name}
            />
        </div>
    );
};

export default CommonPartEditor;
